// src/features/facturas/HistorialFacturasPag.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSync,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
} from "react-icons/fa";

export default function HistorialFacturasPag() {
  const navigate = useNavigate();

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [tamano, setTamano] = useState(15);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const [usuarios, setUsuarios] = useState([]);

  const [filtros, setFiltros] = useState({
    desde: "",
    hasta: "",
    metodoPago: "",
    pago: "",
    mesa: "",
    mesero: "",
    pedidoId: "",
    sort: "fechaEmision,desc",
  });

  const onFiltro = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const cargarDatos = async (pag = pagina) => {
    setCargando(true);
    try {
      const params = { page: pag, size: tamano, sort: filtros.sort };
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.metodoPago) params.metodoPago = filtros.metodoPago;
      if (filtros.pago !== "") params.pago = filtros.pago === "true";
      if (filtros.mesa) params.mesa = filtros.mesa;
      if (filtros.mesero) params.mesero = filtros.mesero;
      if (filtros.pedidoId) params.pedidoId = filtros.pedidoId;

      const res = await axios.get("/facturas/historial", { params });
      const page = res.data;
      setFilas(page.content || []);
      setPagina(page.number);
      setTamano(page.size);
      setTotalPaginas(page.totalPages);
      setTotalRegistros(page.totalElements);
    } catch (e) {
      console.error(e);
      toast.error("Error cargando historial de facturas");
    } finally {
      setCargando(false);
    }
  };

  const cargarUsuarios = async () => {
    try {
      const res = await axios.get("/usuarios");
      setUsuarios(res.data || []);
    } catch (e) {
      console.error("Error cargando usuarios para filtro de mesero:", e);
    }
  };

  useEffect(() => {
    cargarDatos(0);
    cargarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarFiltros = () => cargarDatos(0);

  const paginador = useMemo(() => {
    const items = [];
    for (let i = 0; i < totalPaginas; i++) {
      items.push(i);
    }
    return items;
  }, [totalPaginas]);

  const goToPage = (p) => {
    if (p < 0 || p >= totalPaginas) return;
    cargarDatos(p);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mt-3 bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6">
        {/* Título */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
              Historial de facturas
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Consulta y filtra las facturas emitidas.
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/70 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FaFilter className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Filtros
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Desde */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Desde
              </label>
              <input
                type="datetime-local"
                value={
                  filtros.desde
                    ? new Date(filtros.desde).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onFiltro(
                    "desde",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : ""
                  )
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Hasta */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Hasta
              </label>
              <input
                type="datetime-local"
                value={
                  filtros.hasta
                    ? new Date(filtros.hasta).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onFiltro(
                    "hasta",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : ""
                  )
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Método de pago */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Método de Pago
              </label>
              <select
                value={filtros.metodoPago}
                onChange={(e) => onFiltro("metodoPago", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="TRANSFERENCIA">Transferencia</option>
              </select>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Estado
              </label>
              <select
                value={filtros.pago}
                onChange={(e) => onFiltro("pago", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="true">Pagada</option>
                <option value="false">Pendiente</option>
              </select>
            </div>

            {/* Mesa */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mesa
              </label>
              <input
                placeholder="Número"
                value={filtros.mesa}
                onChange={(e) => onFiltro("mesa", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Mesero */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mesero (nombre)
              </label>
              <select
                value={filtros.mesero}
                onChange={(e) => onFiltro("mesero", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.nombre}>
                    {u.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Pedido ID */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Pedido ID
              </label>
              <input
                type="number"
                value={filtros.pedidoId}
                onChange={(e) => onFiltro("pedidoId", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Sort + botones */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
            <select
              value={filtros.sort}
              onChange={(e) => onFiltro("sort", e.target.value)}
              className="w-full sm:w-48 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
            >
              <option value="fechaEmision,desc">Fecha ↓</option>
              <option value="fechaEmision,asc">Fecha ↑</option>
              <option value="id,desc">ID ↓</option>
              <option value="id,asc">ID ↑</option>
            </select>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => cargarDatos(pagina)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <FaSync className="text-sky-500" />
                Actualizar
              </button>
              <button
                onClick={aplicarFiltros}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="mt-3">
          {cargando ? (
            <div className="flex justify-center py-8">
              <div className="w-7 h-7 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            // 👇 contenedor con scroll horizontal
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800">
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[950px] md:min-w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-950/80">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                        ID
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Fecha Emisión
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Método Pago
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Total
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Estado
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Pedido
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Mesa
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Mesero
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 ? (
                      <tr>
                        <td
                          colSpan="9"
                          className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                        >
                          Sin resultados
                        </td>
                      </tr>
                    ) : (
                      filas.map((f) => (
                        <tr
                          key={f.id}
                          className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          <td className="px-4 py-2 text-left align-middle font-medium">
                            {f.id}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {new Date(f.fechaEmision).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {f.metodoPago}
                          </td>
                          <td className="px-4 py-2 text-center align-middle font-semibold">
                            ${f.total?.toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {f.pago ? "Pagada" : "Pendiente"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {f.pedido?.id}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {f.pedido?.mesa?.numero ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {f.pedido?.mesero?.nombre ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            <button
                              onClick={() => navigate(`/facturas/${f.id}`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-slate-900 transition-colors"
                            >
                              <FaEye />
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Página {totalPaginas === 0 ? 0 : pagina + 1} de {totalPaginas} —{" "}
            {totalRegistros} registros
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(pagina - 1)}
              disabled={pagina <= 0}
              className={`px-2.5 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${
                pagina <= 0
                  ? "border-slate-300 text-slate-300 cursor-not-allowed"
                  : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <FaChevronLeft />
            </button>

            {paginador.map((p) => (
              <button
                key={p}
                onClick={() => goToPage(p)}
                className={`px-2.5 py-1.5 rounded-lg border text-xs ${
                  p === pagina
                    ? "bg-sky-500 border-sky-500 text-white"
                    : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                {p + 1}
              </button>
            ))}

            <button
              onClick={() => goToPage(pagina + 1)}
              disabled={pagina >= totalPaginas - 1}
              className={`px-2.5 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${
                pagina >= totalPaginas - 1 || totalPaginas === 0
                  ? "border-slate-300 text-slate-300 cursor-not-allowed"
                  : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
